import React, { useRef, useState } from "react";
import { MESSAGE_MODALS } from "../../context/custom/MessageModalContext";
import { ALERT_TYPE, MASK_FORMAT, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from "../../config/apis";
import { ServerRequest } from "../../utils/apiweb";
import { useAccess, useManagedContext, useNav, useFilterPersistence, useNavigateWithFilters } from "../../components/hooks";
import {
    Loading,
    TableCustom,
    SectionHeading,
    WrapperAdvancedSearch,
} from "../../components/common";
import DataTaggerModalApi from "../../components/controls/DataTaggerModalApi";
import { standardRequest } from "../../utils/requests";
import {TIPS} from "../../consts/tips";

function EmisionDefinicionesView() {
    const navigate = useNav();
    const { setIsLoading, showMessageModal } = useManagedContext();

    const [state, setState] = useState({
        dataTagger: {
            showModal: false,
            idEntidad: null,
        },
        list: [],
    });

    const [hasEditAccess, setHasEditAccess] = useState(false);
    const [tableVisible, setTableVisible] = useState(false);

    const searchRef = useRef();
    const tableRef = useRef();

    const SearchEmisionDefinicionesWithSavedFilters = () => {
        if (searchRef.current) {
            SearchEmisionDefiniciones();
        }
    };

    const { getCurrentFilters, restoredPageIndex, restoredGlobalFilter } = useFilterPersistence(
        searchRef,
        tableRef,
        SearchEmisionDefinicionesWithSavedFilters
    );

    const navigateWithFilters = useNavigateWithFilters(getCurrentFilters);

    useAccess({
        key: "EmisionDefinicionView",
        onLoaded: (data, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            else setHasEditAccess(data.some((x) => x.codigo === "emision-definicion_edit"));
        },
    });

    const cellA = (props) =>
        !hasEditAccess ? (
            ""
        ) : (
            <div className="action">
                <div onClick={handleClickEmisionDefinicionAdd} className="link">
          <span className="material-symbols-outlined" title="Nuevo">
            add
          </span>
                </div>
            </div>
        );

    const cellVMR = (props) => (
        <div className="action">
            <div onClick={() => handleClickEmisionDefinicionView(props.value)} className="link">
        <span className="material-symbols-outlined" title="Ver">
          search
        </span>
            </div>
            {hasEditAccess && (
                <>
                    <div onClick={() => handleClickEmisionDefinicionModify(props.value)} className="link">
            <span className="material-symbols-outlined" title="Modificar">
              stylus
            </span>
                    </div>
                    <div onClick={() => handleClickEmisionDefinicionClone(props.row.original)} className="link">
            <span className="material-symbols-outlined" title="Clonar">
              content_copy
            </span>
                    </div>
                    <div onClick={() => handleClickEmisionDefinicionRemove(props.row.original)} className="link">
            <span className="material-symbols-outlined" title="Eliminar">
              delete
            </span>
                    </div>
                </>
            )}
            <div onClick={() => handleClickEmisionDefinicionDataTagger(props.value)} className="link">
        <span className="material-symbols-outlined" title="Información adicional">
          info
        </span>
            </div>
        </div>
    );

    const tableColumns = [
        { Header: "Tipo de tributo", accessor: "tipoTributo", width: "15%" },
        { Header: "Numeración", accessor: "numeracion", width: "15%" },
        { Header: "Número", accessor: "numero", width: "10%" },
        { Header: "Descripción", accessor: "descripcion", width: "34%" },
        { Header: "Período", accessor: "periodo", width: "6%" },
        { Header: "Estado", accessor: "estadoEmisionDefinicion", width: "10%" },
        {
            Header: cellA,
            Cell: cellVMR,
            id: "abm",
            accessor: "id",
            width: hasEditAccess ? "10%" : "5%",
            disableGlobalFilter: true,
            disableSortBy: true,
        },
    ];

    const handleClickEmisionDefinicionAdd = () => {
        navigateWithFilters(`/emision-definicion/${OPERATION_MODE.NEW}`);
    };
    const handleClickEmisionDefinicionView = (id) => {
        navigateWithFilters(`/emision-definicion/${OPERATION_MODE.VIEW}/${id}`);
    };
    const handleClickEmisionDefinicionModify = (id) => {
        navigateWithFilters(`/emision-definicion/${OPERATION_MODE.EDIT}/${id}`);
    };
    const handleClickEmisionDefinicionClone = (row) => {
        showMessageModal({
            title: "Confirmación",
            message: `¿Está seguro de clonar la definición ${row.numero}?`,
            onConfirm: () => {
                setIsLoading(true);
                standardRequest(
                    REQUEST_METHOD.PUT,
                    `${APIS.URLS.EMISION_DEFINICION}/clone/${row.id}`
                )
                    .then(() => SearchEmisionDefiniciones())
                    .catch(() => {})
                    .finally(() => setIsLoading(false));
            },
        });
    };
    const handleClickEmisionDefinicionRemove = (row) => {
        showMessageModal({
            ...MESSAGE_MODALS.BORRAR,
            message: `¿Está seguro de borrar la definición de emisión ${row.numero}?`,
            onConfirm: () => {
                setIsLoading(true);
                standardRequest(
                    REQUEST_METHOD.DELETE,
                    `${APIS.URLS.EMISION_DEFINICION}/${row.id}`
                )
                    .then(() => SearchEmisionDefiniciones())
                    .catch(() => {})
                    .finally(() => setIsLoading(false));
            },
        });
    };
    const handleClickEmisionDefinicionDataTagger = (id) => {
        setState((prevState) => ({
            ...prevState,
            dataTagger: { showModal: true, idEntidad: id, disabled: !hasEditAccess },
        }));
    };

    const callbackNoSuccess = (response) => {
        response
            .json()
            .then((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
                setState((prevState) => ({ ...prevState, loading: false }));
            })
            .catch((error) => {
                ShowToastMessage(
                    ALERT_TYPE.ALERT_ERROR,
                    "Error procesando respuesta: " + error
                );
                setState((prevState) => ({ ...prevState, loading: false }));
            });
    };
    const callbackError = (error) => {
        ShowToastMessage(
            ALERT_TYPE.ALERT_ERROR,
            "Error procesando solicitud: " + error.message
        );
        setState((prevState) => ({ ...prevState, loading: false }));
    };

    function SearchEmisionDefiniciones() {
        setState((prevState) => ({ ...prevState, loading: true, list: [] }));

        const { idTipoTributo, idEstadoEmisionDefinicion, numero, periodo, descripcion, etiqueta } =
        searchRef.current?.filters || {};

        const callbackSuccess = (response) => {
            response
                .json()
                .then((data) => {
                    setState((prevState) => ({ ...prevState, loading: false, list: data }));
                    setTableVisible(true);
                })
                .catch((error) => {
                    ShowToastMessage(
                        ALERT_TYPE.ALERT_ERROR,
                        "Error procesando respuesta: " + error
                    );
                    setState((prevState) => ({ ...prevState, loading: false }));
                });
        };

        const paramsUrl =
            `/filter?` +
            `idTipoTributo=${idTipoTributo || 0}&` +
            `idEstadoEmisionDefinicion=${idEstadoEmisionDefinicion || 0}&` +
            `numero=${encodeURIComponent(numero || "")}&` +
            `periodo=${encodeURIComponent(periodo || "")}&` +
            `descripcion=${encodeURIComponent(descripcion || "")}&` +
            `etiqueta=${encodeURIComponent(etiqueta || "")}`;

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.EMISION_DEFINICION,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    const onSearch = () => {
        SearchEmisionDefiniciones();
    };

    const dataTaggerRegistro = state.list.find(x => x.id === state.dataTagger.idEntidad)?.descripcion ?? ''

    return (
        <>
            <Loading visible={state.loading} />

            {state.dataTagger.showModal && (
                <DataTaggerModalApi
                    title="Información adicional de definición de emisiones"
                    registro={dataTaggerRegistro}
                    tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_DEFINICION_EMISIONES_MODAL}
                    entidad="EmisionDefinicion"
                    idEntidad={state.dataTagger.idEntidad}
                    disabled={state.dataTagger.disabled}
                    onConfirm={() => {
                        setState((prevState) => ({
                            ...prevState,
                            dataTagger: { showModal: false, idEntidad: null },
                        }));
                    }}
                    onDismiss={() => {
                        setState((prevState) => ({
                            ...prevState,
                            dataTagger: { showModal: false, idEntidad: null },
                        }));
                    }}
                />
            )}

            <SectionHeading
                titles={[{ title: "Emisión" }, { title: "Definición de emisiones" }]}
                onAddClick={hasEditAccess? handleClickEmisionDefinicionAdd : null}
                tableVisible={tableVisible}
            />

            <section className="section-accordion">
                <WrapperAdvancedSearch
                    ref={searchRef}
                    filters={[
                        {
                            title: "Tipo de tributo",
                            field: "idTipoTributo",
                            type: "list",
                            lista: "TipoTributo",
                            size: 2,
                        },
                        {
                            title: "Estado",
                            field: "idEstadoEmisionDefinicion",
                            type: "list",
                            lista: "EstadoEmisionDefinicion",
                            size: 2,
                        },
                        {
                            title: "Número definición",
                            field: "numero",
                            type: "string",
                            size: 2,
                            mask: MASK_FORMAT.EMISION_NUMERO,
                            searchOnEnter: true,
                        },
                        {
                            title: "Período",
                            field: "periodo",
                            type: "string",
                            size: 2,
                            searchOnEnter: true,
                        },
                        {
                            title: "Descripción",
                            field: "descripcion",
                            type: "string",
                            size: 4,
                            searchOnEnter: true,
                        },
                        {
                            title: "Etiqueta",
                            field: "etiqueta",
                            type: "string",
                            size: 2,
                            searchOnEnter: true,
                        },
                    ]}
                    onSearch={onSearch}
                />

                {tableVisible && (
                    <TableCustom
                        ref={tableRef}
                        restoredPageIndex={restoredPageIndex}
                        defaultGlobalFilter={restoredGlobalFilter}
                        className={"TableCustomBase m-top-20"}
                        columns={tableColumns}
                        data={state.list}
                        messageEmpty="No se encontraron resultados"
                    />
                )}
            </section>
        </>
    );
}

export default EmisionDefinicionesView;
